import FormModal from 'LuxKit/Modal/FormModal'
import TextLink from 'LuxKit/TextLink'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import React, { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import requestLoyaltyTierUpgradeClaim from 'api/loyalty/requestLoyaltyTierUpgradeClaim'
import type { LoyaltyTierUpgradeClaimResult } from 'api/loyalty/requestLoyaltyTierUpgradeClaim'
import { formToObject } from 'lib/forms/formToObject'
import { useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import useModalCloseOnLocationChange from 'hooks/Modal/useModalCloseOnLocationChange'

interface ApiError {
  status: number
  message: string
  errorCode?: string
}

function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'status' in err
}

interface Props {
  campaign: App.TierUpgradeCampaignPayload
}

function TierUpgradeCampaignPromoModal(props: Props) {
  const { campaign } = props
  const intl = useIntl()
  useModalCloseOnLocationChange()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<React.ReactNode>()
  const claimCampaign = useAppSelector(state => state.loyalty.claimCampaigns.data?.[campaign.campaignId])

  const allowedDomainsText = intl.formatList((claimCampaign?.emailVerificationDomains ?? []).map(domain => `@${domain}`), {
    type: 'disjunction',
    style: 'short',
  })

  const emailDomainNotAllowedMessage = allowedDomainsText
    ? intl.formatMessage({
        id: 'u--jQdF1vycLRJsiLB3a',
        defaultMessage: 'This offer is only available to customers with an eligible email address. To access it, your Luxury Escapes account must be registered with one of the following domains: {allowedDomains}.',
        description: 'Message listing the allowed account email domains after a tier upgrade campaign rejects an account email',
      }, { allowedDomains: allowedDomainsText })
    : intl.formatMessage({
        id: 'byFwLNEv2cfyE1av4otP',
        defaultMessage: 'This offer is only available to customers with an eligible email address.',
        description: 'Message shown after a tier upgrade campaign rejects an account email when allowed domains are unavailable',
      })

  const emailVerificationMessage = useMemo(
    () => <FormattedMessage
      id="tkqvNDjlmC79p6ZLnU6D"
      defaultMessage="To redeem this code, <link>verify your email address</link>."
      description="Message linking a tier upgrade campaign customer to verify their account email"
      values={{
        link: (chunks: React.ReactNode) => <TextLink to="/account" size="medium" weight="regular">{chunks}</TextLink>,
      }}
    />,
    [],
  )

  const onSubmit = useCallback(async(
    event: React.FormEvent<HTMLFormElement>,
    resolve: (result?: LoyaltyTierUpgradeClaimResult) => void,
  ) => {
    const { promoCode } = formToObject<{ promoCode: string }>(event.currentTarget)
    setError(undefined)
    setLoading(true)
    try {
      const result = await requestLoyaltyTierUpgradeClaim(campaign.campaignId, promoCode)
      resolve(result)
    } catch(err) {
      const errorCode = isApiError(err) ? err.errorCode : undefined
      const status = isApiError(err) ? err.status : undefined
      let message: React.ReactNode
      if (errorCode === 'ACCOUNT_NOT_FOUND') {
        message = intl.formatMessage({
          id: 'v-mECGmgZjOoD5bGlPuG',
          defaultMessage: 'You need to join Société to redeem this code.',
          description: 'Error message when user has no loyalty account on tier upgrade campaign',
        })
      } else if (errorCode === 'CLAIMS_EMAIL_NOT_VERIFIED') {
        message = emailVerificationMessage
      } else if (errorCode === 'CLAIMS_EMAIL_ELIGIBILITY_UNAVAILABLE') {
        message = intl.formatMessage({
          id: 'BMnx6VWDBaqZ44suUc1b',
          defaultMessage: 'We couldn’t check whether your email address is eligible. Please try again later.',
          description: 'Error shown when tier upgrade campaign email eligibility could not be loaded',
        })
      } else if (errorCode === 'CLAIMS_EMAIL_DOMAIN_NOT_ALLOWED') {
        message = emailDomainNotAllowedMessage
      } else if (errorCode === 'TIER_ALREADY_HIGHER') {
        message = intl.formatMessage({
          id: 'pjMLgAaMOLx72kCSTTMr',
          defaultMessage: 'You cannot redeem a tier lower than your current tier.',
          description: 'Error message when user already has a higher tier on tier upgrade campaign',
        })
      } else if (status === 409) {
        message = intl.formatMessage({
          id: 'l5l-UdFD2FSfNwR35qI9',
          defaultMessage: 'You have already redeemed this benefit.',
          description: 'Error message for duplicate claim on tier upgrade campaign',
        })
      } else if (status === 400) {
        message = intl.formatMessage({
          id: 'uBhwII10LK8AZZCioWaF',
          defaultMessage: 'Invalid or expired code. Please try again.',
          description: 'Error message for invalid promo code on tier upgrade campaign',
        })
      } else if (status === 404) {
        message = intl.formatMessage({
          id: '6VrJ4UpVrVYZevt_Sscp',
          defaultMessage: 'This campaign is no longer available.',
          description: 'Error message for campaign not found on tier upgrade campaign',
        })
      } else {
        message = intl.formatMessage({
          id: 'HL73ONR5Ct2b1DJ3LPvx',
          defaultMessage: 'Something went wrong. Please try again.',
          description: 'Fallback error message for tier upgrade campaign promo code redemption',
        })
      }
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [campaign.campaignId, emailDomainNotAllowedMessage, emailVerificationMessage, intl])

  const title = intl.formatMessage({
    id: '_1FFREPK846A6JLbCKEd',
    defaultMessage: 'Enter in your {partnerName} {memberStatus} details',
    description: 'Title for the tier upgrade campaign partner promo code entry modal',
  }, {
    partnerName: campaign.partner.name,
    memberStatus: campaign.partner.memberStatus,
  })

  const promoCodeLabel = intl.formatMessage({
    id: 'X6r96Xd_9mrtEh404f25',
    defaultMessage: 'Promo code',
    description: 'Label for promo code input on tier upgrade campaign modal',
  })

  const promoCodePlaceholder = intl.formatMessage({
    id: 'ZvRG7XhdO7sQgiYnaFOy',
    defaultMessage: 'EXAMPLE123',
    description: 'Placeholder for promo code input on tier upgrade campaign modal',
  })

  const promoCodeHelpText = intl.formatMessage({
    id: 'T1rQoRSjOGd6V3v4bsbv',
    defaultMessage: 'Check your email or partner communication for your unique code',
    description: 'Help text for promo code input on tier upgrade campaign modal',
  })

  const redeemText = intl.formatMessage({
    id: '24uzhgyx2r5rA7leQpJK',
    defaultMessage: 'Redeem',
    description: 'Button text to redeem promo code on tier upgrade campaign modal',
  })

  return <FormModal
    title={title}
    size="S"
    primaryActionText={redeemText}
    isPrimaryActionDisabled={loading}
    onSubmit={onSubmit}
  >
    <Group direction="vertical" gap={8}>
      <TextInput
        label={promoCodeLabel}
        name="promoCode"
        placeholder={promoCodePlaceholder}
        helpText={promoCodeHelpText}
        required
      />
      {error && <MessageBanner kind="critical" density="compact">{error}</MessageBanner>}
    </Group>
  </FormModal>
}

export default TierUpgradeCampaignPromoModal
